import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { HeartHandshake, CheckCircle2, ShieldCheck } from "lucide-react";

export default function VolunteerApply() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", interests: "", availability: "", message: "", website: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const cls = "w-full border border-[#1F4D2E]/20 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4D2E]";

  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { await api.post("/volunteer/apply", form); setDone(true); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="volunteer-apply-done">
      <CheckCircle2 className="mx-auto text-[#1F4D2E] mb-4" size={44} />
      <h1 className="font-display text-3xl font-bold text-[#1F4D2E] mb-3">Thank you for applying</h1>
      <p className="text-[#4F6B54]">Your volunteer application has been received. Our team will review it, and if approved you'll receive a secure invitation by email to set up your volunteer account.</p>
      <Link to="/" className="inline-block mt-6 bg-[#1F4D2E] text-white rounded-full px-6 py-3 font-semibold">Back to home</Link>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-6 py-16" data-testid="volunteer-apply-page">
      <div className="flex items-center gap-2 text-[#C1272D] mb-3"><HeartHandshake size={22} /><p className="text-xs tracking-[0.2em] uppercase font-bold">Volunteer with us</p></div>
      <h1 className="font-display text-4xl font-bold text-[#1F4D2E] mb-3">Apply to volunteer</h1>
      <p className="text-[#4F6B54] mb-3">Tell us a little about yourself and how you'd like to help. Volunteer access is reviewed and approved before activation — applying does not create an account.</p>
      <p className="flex items-center gap-2 text-xs text-[#4F6B54] mb-8"><ShieldCheck size={14} /> Approved volunteers set a password and enroll in two-factor authentication before activation.</p>
      {error && <div className="bg-red-50 text-red-700 text-sm rounded-md px-4 py-3 mb-4" data-testid="volunteer-apply-error">{error}</div>}
      <form onSubmit={submit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <input required value={form.name} onChange={set("name")} placeholder="Full name" data-testid="vol-name" className={cls} />
          <input required type="email" value={form.email} onChange={set("email")} placeholder="Email" data-testid="vol-email" className={cls} />
          <input value={form.phone} onChange={set("phone")} placeholder="Phone / WhatsApp" data-testid="vol-phone" className={cls} />
          <input value={form.location} onChange={set("location")} placeholder="Location (city, country)" data-testid="vol-location" className={cls} />
        </div>
        <input value={form.interests} onChange={set("interests")} placeholder="Areas of interest (e.g. education, events, fundraising)" data-testid="vol-interests" className={cls} />
        <input value={form.availability} onChange={set("availability")} placeholder="Availability" data-testid="vol-availability" className={cls} />
        <textarea value={form.message} onChange={set("message")} rows={4} placeholder="Why would you like to volunteer with Refugium Ethiopia?" data-testid="vol-message" className={cls} />
        {/* honeypot — hidden from humans, catches bots */}
        <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} name="website" aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} />
        <button disabled={busy} data-testid="vol-submit" className="w-full bg-[#C1272D] text-white rounded-full py-3 font-bold hover:bg-[#9E1F24] transition-colors disabled:opacity-50">Submit application</button>
      </form>
    </div>
  );
}
